import type { ScanFieldKey, ScanMerge } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';

type UseRow = ScanMerge['useRows'][number];

export interface ScanUseRowView {
  field: ScanFieldKey;
  /** The form's own label for the field. */
  label: string;
  /** Display strings: the caller formats amounts, dates and categories. */
  mine: string;
  theirs: string;
  row: UseRow;
}

interface ScanUsePanelProps {
  rows: ScanUseRowView[];
  onUse: (row: UseRow) => void;
  onKeepMine: () => void;
}

/** The receipt's values for the fields the person had already filled. */
export function ScanUsePanel({ rows, onUse, onKeepMine }: ScanUsePanelProps) {
  const { t } = useTranslation();
  if (rows.length === 0) return null;

  return (
    <section
      className="mt-3 rounded-xl border border-gray-200 dark:border-gray-700 p-3"
      aria-label={t('expenses:scan.differs')}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p className="min-w-[45%] flex-1 text-sm font-semibold text-gray-900 dark:text-gray-100">
          {t('expenses:scan.differs')}
        </p>
        <button
          type="button"
          onClick={onKeepMine}
          className="ml-auto rounded-md px-2 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50 dark:text-primary-300 dark:hover:bg-primary-500/15"
        >
          {t('expenses:scan.keepMine')}
        </button>
      </div>
      <ul className="mt-2 space-y-2">
        {rows.map(view => (
          <li
            key={view.field}
            data-testid={`scan-use-row-${view.field}`}
            className="flex flex-wrap items-center gap-x-3 gap-y-1"
          >
            <span className="min-w-[45%] flex-1 text-sm text-gray-900 dark:text-gray-100">
              {view.label}
            </span>
            <span className="text-sm text-gray-500 dark:text-gray-400 break-words">
              {t('expenses:scan.yoursVsReceipt', {
                mine: view.mine,
                theirs: view.theirs,
              })}
            </span>
            <button
              type="button"
              onClick={() => onUse(view.row)}
              aria-label={t('expenses:scan.useA11y', {
                field: view.label,
                value: view.theirs,
              })}
              className="ml-auto rounded-md border border-primary-500 px-3 py-1 text-sm font-medium text-primary-700 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-300 dark:hover:bg-primary-500/15"
            >
              {t('expenses:scan.use')}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
